/**
 * v0.12.0 — Age-tier helpers shared between frontend, backend, and sysadmin.
 *
 * The kid app is a multi-year companion (memory: "App vision: multi-animal,
 * multi-year companion"). Content tone, chat companion voice, UI hints, and
 * quiz difficulty all flex with the child's actual age. Rather than scatter
 * `if (age < 7) ...` ladders across the codebase, every consumer reads from
 * one source of truth: `getAgeTier(age)`.
 *
 * Tiers (in chronological order — keep this order stable; downstream sorts
 * rely on it):
 *
 *   little — 3-5   (preschool, very simple words, lots of emoji)
 *   young  — 6-8   (lower primary, current default tone)
 *   older  — 9-11  (upper primary, fuller sentences, light wordplay)
 *   tween  — 12-14 (early secondary, near-adult phrasing, no baby talk)
 *
 * Anything outside [3, 14] clamps to the nearest band so we never produce
 * undefined behavior — the child app forbids registration outside that
 * range, but legacy rows / dev fixtures occasionally appear with stray
 * birth_dates and we don't want to throw at runtime.
 */

export type AgeTier = 'little' | 'young' | 'older' | 'tween';

export const AGE_TIERS: readonly AgeTier[] = ['little', 'young', 'older', 'tween'] as const;

/** Default tier when birth_date is missing — matches the prior hardcoded
 *  9yo tone, so behavior pre-migration is preserved for existing kids. */
export const DEFAULT_AGE_TIER: AgeTier = 'young';

/**
 * Convert an integer age to a tier band. `null`/`undefined`/non-finite
 * input falls back to DEFAULT_AGE_TIER so callers don't need to guard.
 */
export function getAgeTier(age: number | null | undefined): AgeTier {
  if (age == null || !Number.isFinite(age)) return DEFAULT_AGE_TIER;
  if (age <= 5) return 'little';
  if (age <= 8) return 'young';
  if (age <= 11) return 'older';
  return 'tween';
}

/**
 * Same as getAgeTier but accepts a YYYY-MM-DD string (children.birth_date
 * column shape). Empty / malformed strings return DEFAULT_AGE_TIER.
 */
export function getAgeTierFromBirthDate(birthDate: string | null | undefined): AgeTier {
  return getAgeTier(ageFromBirthDate(birthDate));
}

/**
 * Compute whole-number age from a YYYY-MM-DD birth date. Returns null on
 * any parsing problem. Mirrors `backend/src/lib/validate.js#ageFromBirthDate`
 * — duplicated here so the helper is usable from frontend without pulling
 * in the backend module.
 */
export function ageFromBirthDate(birthDate: string | null | undefined): number | null {
  if (!birthDate) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthDate);
  if (!m) return null;
  const y = Number(m[1]); const mo = Number(m[2]); const d = Number(m[3]);
  if (!y || !mo || !d) return null;
  const today = new Date();
  let age = today.getUTCFullYear() - y;
  const monthDiff = today.getUTCMonth() + 1 - mo;
  if (monthDiff < 0 || (monthDiff === 0 && today.getUTCDate() < d)) age -= 1;
  if (age < 0 || age > 150) return null;
  return age;
}

/**
 * Human-readable label for an age tier — for parent admin / sysadmin UI,
 * not for the kid-facing app. ZH only by design (operator-facing).
 */
export function ageTierLabelZH(tier: AgeTier): string {
  switch (tier) {
    case 'little': return '学龄前 (3-5)';
    case 'young':  return '小学低年级 (6-8)';
    case 'older':  return '小学高年级 (9-11)';
    case 'tween':  return '初中 (12-14)';
  }
}
